/**
 * Ikon- og posisjons-cache for plassmarkører.
 *
 * HVORFOR DENNE FINNES:
 *
 * `ClusteredSpotMarkers` bygde tidligere `icon={createCustomIcon(spot, ...)}` og
 * `position={[lat, lon]}` inline i `.map()`. react-leaflet sammenligner disse med
 * IDENTITET, ikke verdi (se node_modules/react-leaflet/lib/Marker.js):
 *
 *   if (props.position !== prevProps.position) marker.setLatLng(props.position);
 *   if (props.icon != null && props.icon !== prevProps.icon) marker.setIcon(props.icon);
 *
 * Et nytt array og et nytt L.divIcon-objekt per render betyr at BEGGE alltid er
 * ulike, så begge kall fyrte på HVER markør ved HVER render. Og begge er dyre:
 *
 *   setIcon   → DivIcon.createIcon → `div.innerHTML = html` — full HTML-reparse av
 *               de 2–8 absoluttposisjonerte, skyggelagte nodene hver nål består av.
 *   setLatLng → markercluster `_childMarkerMoved` → `_moveChild` →
 *               removeLayer() + addLayer() — full ut-og-inn av klyngetreet.
 *
 * Utløseren var kontinuerlig: hver `moveend` sender ny senter/zoom/bounds opp til
 * App, som re-rendrer Map. Med 1 800–3 700 markører (etter at
 * `reduceSpotsForViewport` ble fjernet 16.08.2026) ble hver panorering tusenvis av
 * HTML-reparser og klyngetre-operasjoner på main thread.
 *
 * LØSNINGEN: cache begge på `spot`-objektets identitet i en WeakMap. Firestore gir
 * et NYTT spot-objekt når plassen faktisk endrer seg, så cachen invalideres av seg
 * selv når — og bare når — noe har endret seg. Ingen manuell nøkkel over alle
 * feltene `createCustomIcon` leser (isVerified, ratingAverage, occupiedUntil,
 * nearestHouse, aiSuitability, …), som ville råtnet neste gang et felt ble lagt til.
 *
 * WeakMap betyr også at oppføringene forsvinner sammen med spot-objektene — ingen
 * lekkasje når brukeren panorerer over hele Norge.
 */
import L from 'leaflet';
import { CampingSpot } from '../../types';
import { createCustomIcon } from './MapUtils';

/** Én cache-oppføring per spot-objekt. */
interface IconEntry {
  /** Ikonet slik det ble bygget. */
  icon: L.DivIcon;
  /** Hvilken `isProcessing`-verdi ikonet ble bygget for (spinner vs. vanlig ikon). */
  processing: boolean;
  /** Hvilken `isSelected`-verdi ikonet ble bygget for (større nål + hvit ring). */
  selected: boolean;
}

const iconCache = new WeakMap<CampingSpot, IconEntry>();
const positionCache = new WeakMap<CampingSpot, [number, number]>();

/**
 * Stabilt ikon for en plass. Samme spot-objekt + samme flagg ⇒ samme
 * objektidentitet tilbake, så react-leaflet hopper over `setIcon`.
 *
 * `isProcessing` og `isSelected` er de eneste inputene som ikke kommer fra
 * spot-objektet selv, så de sammenlignes eksplisitt.
 *
 * Ikoner er trygge å dele mellom markører: `L.DivIcon` er en ren beskrivelse, og
 * `createIcon(oldIcon)` får hver markørs EGEN DOM-node som argument. Det er samme
 * mønster som modul-nivå-ikonene i MapUtils (`junctionIcon` m.fl.).
 */
export function getSpotIcon(
  spot: CampingSpot,
  isSelected: boolean,
  isProcessing: boolean,
): L.DivIcon {
  const cached = iconCache.get(spot);
  if (cached && cached.processing === isProcessing && cached.selected === isSelected) {
    return cached.icon;
  }
  const icon = createCustomIcon(spot, isSelected, isProcessing) as L.DivIcon;
  iconCache.set(spot, { icon, processing: isProcessing, selected: isSelected });
  return icon;
}

/**
 * Stabilt posisjons-array for en plass, slik at react-leaflet hopper over
 * `setLatLng` — og markercluster dermed slipper removeLayer/addLayer-runden.
 *
 * Koordinatene sammenlignes mot den cachede verdien: et spot-objekt skal ikke
 * muteres i praksis, men skjer det, er en nål på feil sted en verre feil enn et
 * tapt cache-treff.
 */
export function getSpotPosition(spot: CampingSpot): [number, number] {
  const { latitude, longitude } = spot.location;
  const cached = positionCache.get(spot);
  if (cached && cached[0] === latitude && cached[1] === longitude) return cached;
  const pos: [number, number] = [latitude, longitude];
  positionCache.set(spot, pos);
  return pos;
}

/**
 * `eventHandlers`-objekt per (klikk-handler, plass).
 *
 * SAMME KLASSE FEIL som ikonet: @react-leaflet/core sin `useEventHandlers` har
 * `eventHandlers` i dep-arrayet til en useEffect (se node_modules/@react-leaflet/
 * core/lib/events.js), så et nytt objektliteral per render ga `instance.off(gamle)`
 * + `instance.on(nye)` på HVER markør ved HVER render. Billigere enn en DOM-rebygging,
 * men fortsatt tusenvis av operasjoner per panorering.
 *
 * To nivåer: ytre nøkkel er klikk-funksjonen (bytter den identitet, må vi lage nye
 * handlere som lukker over den riktige), indre er plassen. Med `useEventCallback` på
 * kallsiden er den ytre nøkkelen stabil, så dette er et treff i praksis.
 */
const handlerCache = new WeakMap<object, WeakMap<CampingSpot, { click: (e: L.LeafletMouseEvent) => void }>>();

export function getSpotEventHandlers(
  spot: CampingSpot,
  onSelectSpot: (id: string) => void,
): { click: (e: L.LeafletMouseEvent) => void } {
  let perSpot = handlerCache.get(onSelectSpot);
  if (!perSpot) {
    perSpot = new WeakMap();
    handlerCache.set(onSelectSpot, perSpot);
  }
  const cached = perSpot.get(spot);
  if (cached) return cached;
  const handlers = {
    click: (e: L.LeafletMouseEvent) => {
      L.DomEvent.stop(e);
      onSelectSpot(spot.id);
    },
  };
  perSpot.set(spot, handlers);
  return handlers;
}

/** Kun for test: tømmer ikke WeakMap-ene (det kan man ikke), men eksponerer treff. */
export function __hasCachedIcon(spot: CampingSpot): boolean {
  return iconCache.has(spot);
}

// ---------------------------------------------------------------------------
// Generiske hjelpere for de ANDRE markørlagene
// ---------------------------------------------------------------------------
//
// Samme identitets-felle som for plassmarkørene (se toppen av filen), men lagene
// under har ikke et `spot`-objekt å henge en WeakMap på — ikonet er en ren
// funksjon av noen få skalarer (type, offset, temperatur). Da er en nøkkel-basert
// cache riktig verktøy.
//
// Disse lagene er nettopp de som SLÅR SEG PÅ AV SEG SELV på zoom ≥ 16
// (DETAIL_ZOOM_THRESHOLD): avfallsstasjoner, badeplasser, ladere, wiki. Derfor var
// «tregt når jeg zoomer helt inn» et eget symptom fra samme årsak — rapportert
// 23.08.2026, etter at plassmarkørene alene var fikset.

/**
 * Ikon-cache med strengnøkkel. Kalleren eier nøkkelen og MÅ ta med alt som
 * påvirker utseendet — ellers vises et gammelt ikon.
 *
 * Taket finnes fordi nøkkelrommet er ubundet (offset og temperatur er tall):
 * uten det ville cachen vokst så lenge kartet er åpent. 2000 ikoner er langt over
 * det et viewport trenger og likevel ubetydelig minne.
 */
const MAX_KEYED_ICONS = 2000;
const keyedIcons = new Map<string, L.DivIcon>();

export function cachedIcon(key: string, factory: () => L.DivIcon): L.DivIcon {
  const hit = keyedIcons.get(key);
  if (hit) return hit;
  if (keyedIcons.size >= MAX_KEYED_ICONS) keyedIcons.clear();
  const icon = factory();
  keyedIcons.set(key, icon);
  return icon;
}

/** Antall ikoner i den nøkkel-baserte cachen (for test). */
export function __keyedIconCount(): number {
  return keyedIcons.size;
}

interface HasLocation { location: { latitude: number; longitude: number } }

const pointPositions = new WeakMap<object, [number, number]>();

/**
 * Stabilt `[lat, lon]` for et vilkårlig objekt med `location`. Samme rolle som
 * `getSpotPosition`, men for ladere/wiki/avfall/badeplasser.
 */
export function getPointPosition(item: HasLocation): [number, number] {
  const { latitude, longitude } = item.location;
  const cached = pointPositions.get(item);
  if (cached && cached[0] === latitude && cached[1] === longitude) return cached;
  const pos: [number, number] = [latitude, longitude];
  pointPositions.set(item, pos);
  return pos;
}
